import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, CheckCircle2, Globe, RotateCcw, AlertTriangle, Wallet, ArrowRight } from "lucide-react";
import styles from "./NotificationBell.module.css";
import { useNotifications } from "../../api/dashboard/useDashboardApi";
import { fmtEUR, timeAgo } from "../../utils/moneyFormat";

const PRODUCT_LABEL = {
  Flight: "Flight", Hotel: "Hotel", Transfer: "Transfer", Package: "Package",
  Insurance: "Insurance", CarRental: "Car Rental", Visa: "Visa", Cruise: "Cruise",
};

const TYPE_ICON = {
  online_booking: Globe,
  refund_pending: RotateCcw,
  unpaid_soon: AlertTriangle,
  supplier_due: Wallet,
};

const sevClass = (s) => (s === "danger" ? styles.sevDanger : s === "warning" ? styles.sevWarning : styles.sevInfo);

// Right-hand chip: travel countdown for the money-before-travel items, else time-ago.
const rightLabel = (n) => {
  if (n.type === "unpaid_soon" || n.type === "supplier_due") {
    const d = Number(n.daysUntil);
    if (!Number.isFinite(d)) return "";
    return d <= 0 ? "Today" : `${d}d left`;
  }
  return timeAgo(n.at);
};

// Read/unread lives client-side: a set of notification keys the user has opened or
// cleared, persisted so it survives reloads. State-derived items leave on their own.
const READ_KEY = "sunsky:notificationsRead";
const loadReadKeys = () => {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || "[]").map(String)); }
  catch { return new Set(); }
};
const saveReadKeys = (set) => {
  try { localStorage.setItem(READ_KEY, JSON.stringify([...set].slice(-800))); } catch { /* quota */ }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { items, loading } = useNotifications(60000);
  const [open, setOpen] = useState(false);
  const [readKeys, setReadKeys] = useState(loadReadKeys);
  const wrapRef = useRef(null);
  const prevUnreadRef = useRef(null);
  const [ring, setRing] = useState(false);

  const unreadCount = useMemo(
    () => items.reduce((n, it) => (readKeys.has(String(it.key)) ? n : n + 1), 0),
    [items, readKeys]
  );

  // Ring the bell when the unread count rises (a genuinely new item arrived).
  useEffect(() => {
    if (prevUnreadRef.current != null && unreadCount > prevUnreadRef.current) {
      setRing(true);
      const t = setTimeout(() => setRing(false), 950);
      prevUnreadRef.current = unreadCount;
      return () => clearTimeout(t);
    }
    prevUnreadRef.current = unreadCount;
    return undefined;
  }, [unreadCount]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const markRead = useCallback((key) => {
    setReadKeys((prev) => {
      if (prev.has(String(key))) return prev;
      const next = new Set(prev); next.add(String(key)); saveReadKeys(next); return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    setReadKeys((prev) => {
      const next = new Set(prev);
      items.forEach((it) => next.add(String(it.key)));
      saveReadKeys(next);
      return next;
    });
  }, [items]);

  const openItem = (n) => {
    markRead(n.key);
    setOpen(false);
    if (n.bookingId) navigate(`/bookings/${n.bookingId}`);
  };

  const badgeText = unreadCount > 9 ? "9+" : String(unreadCount);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={`${styles.bell} ${open ? styles.active : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell size={18} strokeWidth={2.1} className={ring ? styles.bellRing : ""} />
        {unreadCount > 0 && <span className={styles.badge}>{badgeText}</span>}
      </button>

      {open && (
        <div className={styles.panel} role="menu">
          <div className={styles.head}>
            <div className={styles.headTitle}>
              Notifications
              {unreadCount > 0 && <span className={styles.headCount}>{unreadCount}</span>}
            </div>
            <button type="button" className={styles.markAll} onClick={markAllRead} disabled={unreadCount === 0}>
              <CheckCheck size={13.5} strokeWidth={2.4} /> Mark all read
            </button>
          </div>

          <div className={styles.list}>
            {loading && items.length === 0 &&
              [0, 1, 2].map((i) => (
                <div key={i} className={styles.card} style={{ cursor: "default" }}>
                  <span className={`${styles.sk} ${styles.skIcon}`} />
                  <div style={{ flex: 1 }}>
                    <span className={`${styles.sk} ${styles.skLine}`} />
                    <span className={`${styles.sk} ${styles.skSub}`} />
                  </div>
                </div>
              ))}

            {!loading && items.length === 0 && (
              <div className={styles.empty}>
                <span className={styles.emptyIcon}><CheckCircle2 size={26} strokeWidth={1.8} /></span>
                <div className={styles.emptyTitle}>You're all caught up</div>
                <div className={styles.emptySub}>Refunds, payments due and new online bookings will show up here.</div>
              </div>
            )}

            {items.map((n) => {
              const Icon = TYPE_ICON[n.type] || Bell;
              const isUnread = !readKeys.has(String(n.key));
              return (
                <div
                  key={n.key}
                  className={`${styles.card} ${isUnread ? styles.unread : styles.read} ${sevClass(n.severity)}`}
                  onClick={() => openItem(n)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openItem(n); } }}
                  role="menuitem"
                  tabIndex={0}
                >
                  <span className={`${styles.iconTile} ${sevClass(n.severity)}`}>
                    <Icon size={17} strokeWidth={2.2} />
                  </span>

                  <div className={styles.body}>
                    <div className={styles.titleRow}>
                      <span className={styles.title}>{n.title}</span>
                      <span className={styles.amount}>{fmtEUR(n.amount)}</span>
                    </div>

                    <div className={styles.sub}>
                      {n.bookingReference && <span className={styles.ref}>{n.bookingReference}</span>}
                      <span className={styles.customer}>{n.customer}</span>
                    </div>

                    <div className={styles.chips}>
                      <span className={`${styles.chip} ${sevClass(n.severity)}`}>{n.meta}</span>
                      {(n.productTypes || []).slice(0, 1).map((t) => (
                        <span key={t} className={styles.tag}>{PRODUCT_LABEL[t] || t}</span>
                      ))}
                      <span className={styles.action}>
                        {rightLabel(n) && <span className={styles.time}>{rightLabel(n)}</span>}
                        <span className={styles.view}>View <ArrowRight size={12} strokeWidth={2.6} /></span>
                      </span>
                    </div>
                  </div>

                  {isUnread && <span className={styles.dot} />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
